import {
  COLLAGE_TEMPLATES,
  CollageFamily,
  getCollageTemplate,
  type CollageTemplateId,
} from '@/content/collageTemplates'
import { getOutputPreset, type OutputPresetId } from '@/content/instagram'
import { toast } from 'sonner'
import {
  DEFAULT_COLLAGE_BACKGROUND,
  CollageBackgroundKind,
  MAX_COLLAGE_BACKGROUND_BLUR,
  MAX_COLLAGE_GAP,
  NodeKind,
  collageCellContext,
  createNodeId,
  createSourceId,
  fitCollageCells,
  firstImageNode,
  findNode,
  imageNodesInOrder,
  intrinsicRatio,
  outputRatio,
  parentNode,
  replaceNode,
  type CollageNode,
  type CollageBackground,
  type CompositionNode,
  type Document,
  type ImageNode,
  type OutputSettings,
  type SourceId,
} from '@/core/document'
import { disposeSource, loadSource, type SourceImage } from '@/core/image'
import { useEditSession } from '@/core/store/editSession'
import { EditAction } from '@/core/history'
import { trackUsage, UsageEvent } from '@/core/reporting'
import { ToolTab } from '@/core/store/uiState'
import {
  FrameStyleId,
  createDefaultPlainFrameParams,
  validatePlainFrameParams,
  type PlainFrameParams,
} from '@/tools/frame/params'

function normalizeForFamily(node: CompositionNode, family: CollageFamily): CompositionNode {
  if (family === CollageFamily.Grid) {
    return node.kind === NodeKind.Frame && node.params.styleId === FrameStyleId.Plain
      ? node.child
      : node
  }
  if (node.kind === NodeKind.Frame && node.params.styleId === FrameStyleId.Plain) return node
  return {
    id: createNodeId(),
    kind: NodeKind.Frame,
    params: createDefaultPlainFrameParams(),
    child: node,
  }
}

function templatePreset(templateId: CollageTemplateId, currentPresetId: OutputPresetId): OutputPresetId {
  const template = getCollageTemplate(templateId)
  if ((template.goodFor as readonly OutputPresetId[]).includes(currentPresetId)) return currentPresetId
  const presetId = template.goodFor[0]
  if (!presetId) throw new Error(`Collage template "${template.id}" has no configured output preset`)
  return presetId
}

function fitTemplate(
  node: CollageNode,
  output: OutputSettings,
  sources: ReturnType<typeof useEditSession.getState>['sources'],
): CollageNode {
  const ratio = outputRatio(output, intrinsicRatio(node, sources))
  return fitCollageCells(node, { width: ratio.w, height: ratio.h }, sources)
}

function replaceCollageInDocument(
  document: Document,
  current: CollageNode,
  next: CollageNode,
  presetId: OutputPresetId,
): Document {
  return {
    ...document,
    output: { ...document.output, presetId },
    root: current.id === document.root.id ? next : replaceNode(document.root, current.id, next),
  }
}

function activeCollage(state: ReturnType<typeof useEditSession.getState>): CollageNode {
  if (!state.document) throw new Error('Collage editing requires a loaded document')
  if (state.selectedNodeId) {
    const selected = findNode(state.document.root, state.selectedNodeId)
    if (!selected) throw new Error(`Active photo "${state.selectedNodeId}" is missing`)
    const context = collageCellContext(state.document.root, selected.id)
    if (context) return context.collage
  }
  if (state.document.root.kind === NodeKind.Collage) return state.document.root
  throw new Error('Collage editing requires a collage document')
}

function updateSelectedCollage(
  updater: (
    collage: CollageNode,
    state: ReturnType<typeof useEditSession.getState>,
    document: Document,
  ) => CollageNode,
  commit: boolean,
): void {
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Collage style update requires a loaded document')
  const selected = activeCollage(state)
  const next = updater(selected, state, state.document)
  state.replaceDocument(
    replaceCollageInDocument(state.document, selected, next, state.document.output.presetId),
    state.selectedNodeId,
  )
  if (commit) useEditSession.getState().commitHistory(EditAction.EditCollageStyle)
}

async function decodeFiles(files: readonly File[]): Promise<SourceImage[]> {
  const decoded: SourceImage[] = []
  try {
    for (const file of files) decoded.push(await loadSource(file))
    return decoded
  } catch (error) {
    decoded.forEach(disposeSource)
    throw error
  }
}

export function enterCollage(): void {
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Entering collage mode requires a loaded document')
  state.setActiveTab(ToolTab.Collage)
  if (state.document.root.kind === NodeKind.Collage) {
    return
  }
  const template = COLLAGE_TEMPLATES.find((candidate) => (
    (candidate.goodFor as readonly OutputPresetId[]).includes(state.document?.output.presetId as OutputPresetId)
  ))
  if (!template) {
    throw new Error(`No collage template is configured for output preset "${state.document.output.presetId}"`)
  }
  const child = normalizeForFamily(state.document.root, template.family)
  const collage: CollageNode = {
    id: createNodeId(),
    kind: NodeKind.Collage,
    templateId: template.id,
    gap: 0,
    background: { ...DEFAULT_COLLAGE_BACKGROUND },
    cells: [child, ...Array.from({ length: template.cells.length - 1 }, () => null)],
    tray: [],
  }
  const fitted = fitTemplate(collage, state.document.output, state.sources)
  state.replaceDocument({ ...state.document, root: fitted }, state.selectedNodeId)
  useEditSession.getState().commitHistory(EditAction.EnterCollage)
}

function stripAutomaticPrintFrame(node: CompositionNode, family: CollageFamily): CompositionNode {
  return family === CollageFamily.Prints
    && node.kind === NodeKind.Frame
    && node.params.styleId === FrameStyleId.Plain
    ? node.child
    : node
}

export function leaveCollage(): void {
  const state = useEditSession.getState()
  if (!state.document || state.document.root.kind !== NodeKind.Collage) {
    throw new Error('Leaving a collage requires a root Collage document')
  }
  const collage = state.document.root
  const activeNodeId = state.selectedNodeId
  const activeCell = activeNodeId
    ? collage.cells.find((child) => child !== null && findNode(child, activeNodeId) !== null)
    : undefined
  const firstFilledCell = collage.cells.find((child): child is CompositionNode => child !== null)
  const keptCell = activeCell ?? firstFilledCell
  if (!keptCell) {
    state.clearSource()
    trackUsage(UsageEvent.CollageLeft, {})
    return
  }

  const template = getCollageTemplate(collage.templateId)
  const keptNode = stripAutomaticPrintFrame(keptCell, template.family)
  const selectedNodeId = activeNodeId && findNode(keptNode, activeNodeId)?.kind === NodeKind.Image
    ? activeNodeId
    : firstImageNode(keptNode).id
  const removedPhotos = imageNodesInOrder(collage).length - imageNodesInOrder(keptNode).length
  state.replaceDocument({ ...state.document, root: keptNode }, selectedNodeId)
  useEditSession.getState().commitHistory(EditAction.LeaveCollage)
  trackUsage(UsageEvent.CollageLeft, {})
  toast.info(`Back to one photo — ${removedPhotos} photos removed`, {
    action: {
      label: 'Undo',
      onClick: () => useEditSession.getState().undo(),
    },
  })
}

export function switchSelectedCollageTemplate(templateId: CollageTemplateId): void {
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Switching a collage template requires a loaded document')
  const selected = activeCollage(state)
  const template = getCollageTemplate(templateId)
  const previousPresetId = state.document.output.presetId
  const presetId = templatePreset(templateId, state.document.output.presetId)
  if (presetId !== state.document.output.presetId && state.document.output.split) {
    throw new Error('A collage template cannot switch output ratio while a split is active')
  }
  const ordered = [...selected.cells.filter((child): child is CompositionNode => child !== null), ...selected.tray]
    .map((child) => normalizeForFamily(child, template.family))
  const next: CollageNode = {
    ...selected,
    templateId,
    gap: template.family === CollageFamily.Prints ? 0 : selected.gap,
    cells: Array.from({ length: template.cells.length }, (_, index) => ordered[index] ?? null),
    tray: ordered.slice(template.cells.length),
  }
  const fitted = fitTemplate(next, { ...state.document.output, presetId }, state.sources)
  state.replaceDocument(replaceCollageInDocument(state.document, selected, fitted, presetId), state.selectedNodeId)
  useEditSession.getState().commitHistory(EditAction.ChangeCollageTemplate)
  if (presetId !== previousPresetId) {
    toast.info(`Changed output to ${getOutputPreset(presetId).label} to support ${template.label}`, {
      action: {
        label: 'Undo',
        onClick: () => useEditSession.getState().undo(),
      },
    })
  }
}

export function restoreSelectedTrayNode(trayIndex: number): void {
  if (!Number.isInteger(trayIndex) || trayIndex < 0) throw new Error('Collage tray index must be a non-negative integer')
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Restoring a collage tray photo requires a loaded document')
  const selected = activeCollage(state)
  const child = selected.tray[trayIndex]
  if (!child) throw new Error(`Collage tray does not contain item ${trayIndex + 1}`)
  const emptyIndex = selected.cells.indexOf(null)
  if (emptyIndex < 0) throw new Error('Collage tray restoration requires an empty cell')
  const cells = [...selected.cells]
  cells[emptyIndex] = child
  const tray = selected.tray.filter((_, index) => index !== trayIndex)
  const fitted = fitTemplate({ ...selected, cells, tray }, state.document.output, state.sources)
  state.replaceDocument(replaceCollageInDocument(
    state.document,
    selected,
    fitted,
    state.document.output.presetId,
  ), state.selectedNodeId)
  useEditSession.getState().commitHistory(EditAction.RestoreCollagePhoto)
}

export async function fillSelectedCollageFiles(
  files: readonly File[],
  preferredCellIndex?: number,
): Promise<void> {
  if (files.length === 0) throw new Error('Filling collage cells requires at least one image file')
  const state = useEditSession.getState()
  const collage = activeCollage(state)
  if (!state.document) throw new Error('Filling collage cells requires a loaded document')
  if (preferredCellIndex !== undefined
    && (!Number.isInteger(preferredCellIndex)
      || preferredCellIndex < 0
      || preferredCellIndex >= collage.cells.length
      || collage.cells[preferredCellIndex] !== null)) {
    throw new Error('The requested collage cell is not an empty placeholder')
  }
  state.setIsLoading(true)
  let decoded: SourceImage[]
  try {
    decoded = await decodeFiles(files)
  } catch (error) {
    useEditSession.getState().setIsLoading(false)
    throw error
  }
  let installed = false
  try {
    if (useEditSession.getState().document !== state.document) {
      throw new Error('The collage changed while the selected photos were decoding')
    }
    const template = getCollageTemplate(collage.templateId)
    const emptyIndices = collage.cells
      .map((child, index) => child === null ? index : -1)
      .filter((index) => index >= 0)
    const targetIndices = preferredCellIndex === undefined
      ? emptyIndices
      : [preferredCellIndex, ...emptyIndices.filter((index) => index !== preferredCellIndex)]
    const cells = [...collage.cells]
    const tray = [...collage.tray]
    const addedSources = new Map<SourceId, SourceImage>()
    let firstPlacedNodeId: ImageNode['id'] | null = null
    for (const [index, source] of decoded.entries()) {
      const sourceId = createSourceId()
      const image: ImageNode = {
        id: createNodeId(),
        kind: NodeKind.Image,
        sourceId,
        transforms: {},
      }
      addedSources.set(sourceId, source)
      const node = normalizeForFamily(image, template.family)
      const targetIndex = targetIndices[index]
      if (targetIndex === undefined) tray.push(node)
      else {
        cells[targetIndex] = node
        firstPlacedNodeId ??= image.id
      }
    }
    const fitted = fitTemplate({ ...collage, cells, tray }, state.document.output, new Map([
      ...state.sources,
      ...addedSources,
    ]))
    state.replaceDocument(
      replaceCollageInDocument(state.document, collage, fitted, state.document.output.presetId),
      firstPlacedNodeId ?? state.selectedNodeId,
      addedSources,
    )
    installed = true
    useEditSession.getState().commitHistory(EditAction.AddCollagePhotos)
  } finally {
    if (!installed) decoded.forEach(disposeSource)
    useEditSession.getState().setIsLoading(false)
  }
}

export async function replaceSelectedCollageImage(file: File): Promise<void> {
  const state = useEditSession.getState()
  if (!state.document || !state.selectedNodeId) {
    throw new Error('Replacing a collage photo requires a selected cell image')
  }
  const selected = findNode(state.document.root, state.selectedNodeId)
  if (selected?.kind !== NodeKind.Image) {
    throw new Error('Replacing a collage photo requires a selected Image node')
  }
  const context = collageCellContext(state.document.root, selected.id)
  if (!context) throw new Error('Replacing a collage photo requires an image inside a collage cell')
  state.setIsLoading(true)
  let decoded: SourceImage[]
  try {
    decoded = await decodeFiles([file])
  } catch (error) {
    useEditSession.getState().setIsLoading(false)
    throw error
  }
  const [source] = decoded
  if (!source) {
    useEditSession.getState().setIsLoading(false)
    throw new Error('Replacing a collage photo did not decode a source image')
  }
  let installed = false
  try {
    const current = useEditSession.getState()
    if (current.document !== state.document || current.selectedNodeId !== selected.id) {
      throw new Error('The selected collage photo changed while its replacement was decoding')
    }
    const sourceId = createSourceId()
    const replacement: ImageNode = { ...selected, sourceId, transforms: {} }
    const cellRoot = replaceNode(context.cellRoot, selected.id, replacement)
    const cells = [...context.collage.cells]
    cells[context.cellIndex] = cellRoot
    const addedSources = new Map([[sourceId, source]])
    const fitted = fitTemplate({ ...context.collage, cells }, state.document.output, new Map([
      ...state.sources,
      ...addedSources,
    ]))
    state.replaceDocument(
      replaceCollageInDocument(state.document, context.collage, fitted, state.document.output.presetId),
      selected.id,
      addedSources,
    )
    installed = true
    useEditSession.getState().commitHistory(EditAction.ReplaceCollagePhoto)
  } finally {
    if (!installed) disposeSource(source)
    useEditSession.getState().setIsLoading(false)
  }
}

export function removeSelectedCollageCell(): void {
  const state = useEditSession.getState()
  if (!state.document || !state.selectedNodeId) {
    throw new Error('Removing a collage photo requires a selected cell image')
  }
  const context = collageCellContext(state.document.root, state.selectedNodeId)
  if (!context) throw new Error('Removing a collage photo requires a node inside a collage cell')
  const cells = [...context.collage.cells]
  cells[context.cellIndex] = null
  const next = { ...context.collage, cells }
  state.replaceDocument(
    replaceCollageInDocument(state.document, context.collage, next, state.document.output.presetId),
    null,
  )
  useEditSession.getState().commitHistory(EditAction.RemoveCollagePhoto)
}

export function swapSelectedCollageCells(sourceIndex: number, targetIndex: number): void {
  if (!Number.isInteger(sourceIndex) || !Number.isInteger(targetIndex)
    || sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    throw new Error('Collage swap requires two different non-negative cell indexes')
  }
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Swapping collage cells requires a loaded document')
  const collage = activeCollage(state)
  if (sourceIndex >= collage.cells.length || targetIndex >= collage.cells.length) {
    throw new Error('Collage swap cell index is outside the selected template')
  }
  if (!collage.cells[sourceIndex]) throw new Error('Collage swap source cell is empty')
  const cells = [...collage.cells]
  const source = cells[sourceIndex]
  cells[sourceIndex] = cells[targetIndex] ?? null
  cells[targetIndex] = source ?? null
  const fitted = fitTemplate({ ...collage, cells }, state.document.output, state.sources)
  state.replaceDocument(
    replaceCollageInDocument(state.document, collage, fitted, state.document.output.presetId),
    state.selectedNodeId,
  )
  useEditSession.getState().commitHistory(EditAction.SwapCollagePhotos)
}

export function setSelectedCollageGap(gap: number, commit = false): void {
  if (!Number.isFinite(gap) || gap < 0 || gap > MAX_COLLAGE_GAP) {
    throw new Error(`Collage gap must be from 0 to ${MAX_COLLAGE_GAP}`)
  }
  updateSelectedCollage((collage, state, document) => {
    const template = getCollageTemplate(collage.templateId)
    if (template.family !== CollageFamily.Grid) throw new Error('Collage gap is available only for Grid templates')
    return fitTemplate({ ...collage, gap }, document.output, state.sources)
  }, commit)
}

function updateOuterPlainFrame(
  node: CompositionNode | null,
  partial: Partial<PlainFrameParams>,
): CompositionNode | null {
  if (node?.kind !== NodeKind.Frame || node.params.styleId !== FrameStyleId.Plain) return node
  const params = { ...node.params, ...partial }
  validatePlainFrameParams(params)
  return { ...node, params }
}

export function setSelectedPrintBorder(borderMm: number, commit = false): void {
  validatePlainFrameParams({ ...createDefaultPlainFrameParams(), borderMm })
  updateSelectedCollage((collage, state, document) => {
    const template = getCollageTemplate(collage.templateId)
    if (template.family !== CollageFamily.Prints) {
      throw new Error('Print border width is available only for Prints templates')
    }
    const cells = collage.cells.map((node) => updateOuterPlainFrame(node, { borderMm }))
    const tray = collage.tray.map((node) => updateOuterPlainFrame(node, { borderMm }) ?? node)
    return fitTemplate({ ...collage, cells, tray }, document.output, state.sources)
  }, commit)
}

export function setSelectedCollageBackground(background: CollageBackground, commit = true): void {
  updateSelectedCollage((collage) => ({ ...collage, background }), commit)
}

export async function setSelectedCollageBackgroundImage(file: File): Promise<void> {
  const state = useEditSession.getState()
  if (!state.document) throw new Error('Collage background image requires a loaded document')
  const collage = activeCollage(state)
  state.setIsLoading(true)
  let decoded: SourceImage[]
  try {
    decoded = await decodeFiles([file])
  } catch (error) {
    useEditSession.getState().setIsLoading(false)
    throw error
  }
  const [source] = decoded
  if (!source) {
    useEditSession.getState().setIsLoading(false)
    throw new Error('Collage background image did not decode a source image')
  }
  let installed = false
  try {
    const current = useEditSession.getState()
    if (current.document !== state.document) {
      throw new Error('The selected collage changed while its background image was decoding')
    }
    const sourceId = createSourceId()
    const next: CollageNode = {
      ...collage,
      background: { kind: CollageBackgroundKind.Image, sourceId, blur: 0 },
    }
    state.replaceDocument(
      replaceCollageInDocument(state.document, collage, next, state.document.output.presetId),
      state.selectedNodeId,
      new Map([[sourceId, source]]),
    )
    installed = true
    useEditSession.getState().commitHistory(EditAction.EditCollageStyle)
  } finally {
    if (!installed) disposeSource(source)
    useEditSession.getState().setIsLoading(false)
  }
}

export function setSelectedCollageBackgroundBlur(blur: number, commit = false): void {
  if (!Number.isFinite(blur) || blur < 0 || blur > MAX_COLLAGE_BACKGROUND_BLUR) {
    throw new Error(`Collage background blur must be from 0 to ${MAX_COLLAGE_BACKGROUND_BLUR} px`)
  }
  updateSelectedCollage((collage) => {
    if (collage.background.kind !== CollageBackgroundKind.Image) {
      throw new Error('Collage background blur requires an image background')
    }
    return { ...collage, background: { ...collage.background, blur } }
  }, commit)
}

export function updateSelectedPlainFrameParams(
  partial: Partial<PlainFrameParams>,
  commit = false,
): void {
  const state = useEditSession.getState()
  if (!state.document || !state.selectedNodeId) {
    throw new Error('Plain frame update requires a selected collage frame')
  }
  const activePhoto = findNode(state.document.root, state.selectedNodeId)
  if (activePhoto?.kind !== NodeKind.Image) throw new Error('Plain frame update requires an active photo')
  const selected = parentNode(state.document.root, activePhoto.id)
  if (selected?.kind !== NodeKind.Frame || selected.params.styleId !== FrameStyleId.Plain) {
    throw new Error('Plain frame update requires the active photo to have a Plain parent frame')
  }
  const context = collageCellContext(state.document.root, selected.id)
  if (!context) throw new Error('Plain frame update requires a frame inside a collage cell')
  const params = { ...selected.params, ...partial }
  validatePlainFrameParams(params)
  const cellRoot = replaceNode(context.cellRoot, selected.id, { ...selected, params })
  const cells = [...context.collage.cells]
  cells[context.cellIndex] = cellRoot
  const fitted = fitTemplate({ ...context.collage, cells }, state.document.output, state.sources)
  state.replaceDocument(
    replaceCollageInDocument(state.document, context.collage, fitted, state.document.output.presetId),
    activePhoto.id,
  )
  if (commit) useEditSession.getState().commitHistory(EditAction.EditCollageStyle)
}
